import React, { useState, useEffect } from "react";
import { Form, Select, Button, DatePicker, InputNumber, Input, Spin, message } from "antd";
import dayjs from "dayjs";
import { fetchTalentJobApplications } from "../../../../utils/dropdownUtils";

const { Option } = Select;
const { TextArea } = Input;

const ContractDetails = ({ initialData, onBack, onSubmit, isLastStep, isEditMode }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [talentApplications, setTalentApplications] = useState([]);
  const [customerOrganizations, setCustomerOrganizations] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(initialData.customerOrganization);
  const [selectedJob, setSelectedJob] = useState(initialData.jobId);
  const [contractDuration, setContractDuration] = useState(initialData.contractDurationMonths || "");
  const [onboardingDate, setOnboardingDate] = useState(
    initialData.onboardingDate ? dayjs(initialData.onboardingDate) : null
  );
  const [lastWorkingDay, setLastWorkingDay] = useState(
    initialData.lastWorkingDay ? dayjs(initialData.lastWorkingDay) : null
  );

  useEffect(() => {
    fetchData();
  }, [initialData.talentId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      if (!initialData.talentId) {
        message.warning("Please select a talent first");
        setLoading(false);
        return;
      }

      const applications = await fetchTalentJobApplications(initialData.talentId);
      setTalentApplications(applications);

      const uniqueCustomers = [...new Set(applications.map(app => app.customerOrganization))].filter(Boolean);
      const customerOptions = uniqueCustomers.map((org, index) => ({
        id: `C${String(index + 1).padStart(3, '0')}`,
        name: org,
      }));
      setCustomerOrganizations(customerOptions);

      const jobOptions = applications.map(app => ({
        id: app.jobId,
        role: app.job?.role || "Unknown",
        customerOrganization: app.customerOrganization,
        applicationId: app.applicationId,
      }));
      setJobs(jobOptions);

    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerChange = (customerOrg) => {
    setSelectedCustomer(customerOrg);
    setSelectedJob("");
    form.setFieldsValue({
      jobId: undefined,
    });
  };

  const handleJobChange = (jobId) => {
    setSelectedJob(jobId);
  };

  const calculateLastWorkingDay = (startDate, duration) => {
    if (!startDate || !duration) return null;

    const durationValue = parseInt(duration);
    if (isNaN(durationValue)) return null;

    const start = dayjs(startDate);
    const end = start.add(durationValue, 'month');
    return end;
  };

  const handleOnboardingDateChange = (date) => {
    setOnboardingDate(date);
    if (date && contractDuration) {
      const calculatedEnd = calculateLastWorkingDay(date, contractDuration);
      setLastWorkingDay(calculatedEnd);
      form.setFieldsValue({
        lastWorkingDay: calculatedEnd,
      });
    }
  };

  const handleContractDurationChange = (value) => {
    setContractDuration(value);
    if (onboardingDate && value) {
      const calculatedEnd = calculateLastWorkingDay(onboardingDate, value);
      setLastWorkingDay(calculatedEnd);
      form.setFieldsValue({
        lastWorkingDay: calculatedEnd,
      });
    }
  };

  const handleBack = () => {
    const currentValues = form.getFieldsValue();
    
    onBack({
      customerOrganization: currentValues.customerOrganization || "",
      jobId: currentValues.jobId || "",
      onboardingDate: currentValues.onboardingDate ? currentValues.onboardingDate.format("YYYY-MM-DD") : null,
      contractDurationMonths: currentValues.contractDurationMonths || "",
      lastWorkingDay: currentValues.lastWorkingDay ? currentValues.lastWorkingDay.format("YYYY-MM-DD") : null,
      monthlyRate: currentValues.monthlyRate || "",
      notes: currentValues.notes || "",
    });
  };

  const handleSubmit = (values) => {
    const selectedApplication = talentApplications.find(app => app.jobId === values.jobId);
    
    onSubmit({
      customerOrganization: values.customerOrganization,
      jobId: values.jobId,
      applicationId: selectedApplication?.applicationId || null,
      onboardingDate: values.onboardingDate ? values.onboardingDate.format("YYYY-MM-DD") : null,
      contractDurationMonths: values.contractDurationMonths,
      lastWorkingDay: values.lastWorkingDay ? values.lastWorkingDay.format("YYYY-MM-DD") : null,
      monthlyRate: values.monthlyRate,
      notes: values.notes,
    });
  };

  const availableJobs = selectedCustomer 
    ? jobs.filter(j => j.customerOrganization === selectedCustomer)
    : [];

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (talentApplications.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <p>This talent has not applied to any jobs yet.</p>
        <Button onClick={() => onBack({})}>Back</Button>
      </div>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        customerOrganization: initialData.customerOrganization,
        jobId: initialData.jobId,
        onboardingDate: initialData.onboardingDate ? dayjs(initialData.onboardingDate) : null,
        contractDurationMonths: initialData.contractDurationMonths,
        lastWorkingDay: initialData.lastWorkingDay ? dayjs(initialData.lastWorkingDay) : null,
        monthlyRate: initialData.monthlyRate,
        notes: initialData.notes,
      }}
    >
      <div className="two-column-grid">
        <Form.Item
          name="customerOrganization"
          label="Customer Organization"
          rules={[{ required: true, message: "Please select a customer" }]}
        >
          <Select
            placeholder="Select Customer"
            onChange={handleCustomerChange}
            showSearch
            filterOption={(input, option) =>
              (option?.children || "").toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            disabled={isEditMode}
          >
            {customerOrganizations.map((customer) => (
              <Option key={customer.id} value={customer.name}>
                {customer.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="jobId"
          label="Job ID - Job Title"
          rules={[{ required: true, message: "Please select a job" }]}
        >
          <Select
            placeholder="Select Job"
            onChange={handleJobChange}
            disabled={!selectedCustomer || isEditMode}
            showSearch
            filterOption={(input, option) =>
              (option?.children || "").toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {availableJobs.map((job) => (
              <Option key={job.id} value={job.id}>
                {job.id} - {job.role}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </div>

      <div className="three-column-grid">
        <Form.Item
          name="onboardingDate"
          label="Onboarding Date"
          rules={[{ required: true, message: "Please select onboarding date" }]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="DD/MM/YYYY"
            placeholder="Select Date"
            onChange={handleOnboardingDateChange}
          />
        </Form.Item>

        <Form.Item
          name="contractDurationMonths"
          label="Contract Duration (Months)"
          rules={[{ required: true, message: "Please enter contract duration" }]}
        >
          <InputNumber
            placeholder="Enter months"
            style={{ width: "100%" }}
            min={1}
            max={120}
            onChange={handleContractDurationChange}
          />
        </Form.Item>

        <Form.Item
          name="lastWorkingDay"
          label="Last Working Day"
        >
          <DatePicker
            style={{ width: "100%" }}
            format="DD/MM/YYYY"
            placeholder="Auto-calculated"
            disabled
          />
        </Form.Item>
      </div>

      <div className="two-column-grid">
        <Form.Item
          name="monthlyRate"
          label="Monthly Rate (Partner Rate)"
          rules={[{ required: true, message: "Please enter monthly rate" }]}
        >
          <InputNumber
            placeholder="Enter amount"
            style={{ width: "100%" }}
            min={0}
            formatter={(value) =>
              `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) => value ? value.replace(/₹\s?|(,*)/g, "") : ""}
          />
        </Form.Item>
      </div>

      <Form.Item
        name="notes"
        label="Notes (Optional)"
      >
        <TextArea
          rows={3}
          placeholder="Add any additional notes..."
        />
      </Form.Item>

      <div className="form-actions">
        <Button onClick={handleBack}>
          Back
        </Button>
        <Button type="primary" htmlType="submit">
          {isEditMode ? "Update Hiring Record" : "Submit Hiring Record"}
        </Button>
      </div>
    </Form>
  );
};

export default ContractDetails;
